import { useState } from "react";
import { MessageSquare, ClipboardEdit } from "lucide-react";
import { SupportChat } from "@/components/support/support-chat";
import { SupportForm } from "@/components/support/support-form";
import { HelpCircle } from "lucide-react";
import {
  SUPPORT_ATTACHED_NOTE,
  SUPPORT_CHAT_PRIVACY,
  SUPPORT_FORM_PRIVACY,
} from "@/lib/support";

/**
 * „Parašyk mums" — the whole of it, mounted by both the page and the modal.
 *
 * Two ways in, and neither is the fallback of the other. The chat can answer
 * outright, which is the best outcome available; the form reaches no AI at all,
 * so it cannot be slow, it works with the provider down, and it is the
 * privacy-safe route — worth saying where somebody is choosing between them.
 *
 * The chat hands over to the form rather than the other way round, carrying
 * whatever was typed: a person who has already described their problem must
 * never have to describe it again.
 *
 * The FAQ sits under both because answering yourself beats either — and it is
 * the same twelve entries the model is given, so the fast route and the slow one
 * cannot contradict each other.
 */
export function SupportPanel({ path, learnerId }: { path?: string; learnerId?: string }) {
  const [mode, setMode] = useState<"chat" | "form">("chat");
  const [carried, setCarried] = useState("");
  // Opened when the model could not answer: the FAQ is then the only thing on
  // screen that can, so it must not be behind another click.
  const [faqOpen, setFaqOpen] = useState(false);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-1 rounded-lg bg-muted/60 p-1 text-sm">
        <button
          type="button"
          onClick={() => setMode("chat")}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 cursor-pointer ${
            mode === "chat" ? "bg-background shadow-sm font-medium" : "text-muted-foreground"
          }`}
        >
          <MessageSquare className="h-3.5 w-3.5" />
          Pasikalbėti
        </button>
        <button
          type="button"
          onClick={() => setMode("form")}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 cursor-pointer ${
            mode === "form" ? "bg-background shadow-sm font-medium" : "text-muted-foreground"
          }`}
        >
          <ClipboardEdit className="h-3.5 w-3.5" />
          Užpildyti formą
        </button>
      </div>

      {mode === "chat" ? (
        <SupportChat
          path={path}
          learnerId={learnerId}
          onSwitchToForm={(text) => {
            setCarried(text);
            setFaqOpen(true);
            setMode("form");
          }}
        />
      ) : (
        <SupportForm initialText={carried} path={path} learnerId={learnerId} />
      )}

      <p className="text-xs text-muted-foreground">{SUPPORT_ATTACHED_NOTE}</p>

      {/* A link, not a fold. The FAQ is its own public page now, so embedding a
          second copy here would be two places to keep in step — and the modal
          was getting long. It opens in a NEW TAB deliberately: somebody halfway
          through describing a problem must not lose what they typed to go and
          read an answer. Highlighted when the model could not answer, because
          the FAQ is then the only thing that can. */}
      <a
        href="/duk"
        target="_blank"
        rel="noreferrer"
        className={`inline-flex items-center gap-1.5 self-start rounded-lg border px-3 py-2 text-sm hover:bg-accent ${
          faqOpen ? "border-primary/60 bg-primary/5 font-medium" : ""
        }`}
      >
        <HelpCircle className="h-4 w-4 text-primary" />
        Dažni klausimai
      </a>

      {/* The warning follows the mode, because the two are not the same warning:
          the chat reaches an outside provider and the form does not. */}
      <p className="text-xs text-muted-foreground">
        {mode === "chat" ? SUPPORT_CHAT_PRIVACY : SUPPORT_FORM_PRIVACY}
      </p>
    </div>
  );
}
